import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, computed, effect } from '@angular/core';
import { VoiceToTextService } from '../../../services/ai/voice-to-text.service';

@Component({
  standalone: false,
  selector: 'sbnb-waveform-simple',
  templateUrl: './waveform-simple.component.html',
})
export class WaveformSimpleComponent implements OnInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('waveformContainer', { static: true }) container!: ElementRef<HTMLDivElement>;

  @Input() sessionId!: string;

  private readonly barWidth: number = 2;
  private readonly barGap: number = 2;
  private readonly minimalAmplitude: number = 0.04;
  private readonly waveColor: string = '#ed3c6a';

  readonly canvasWidth = 128;
  readonly canvasHeight = 32;

  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array | null = null;
  private animationId: number | null = null;

  // Amplitude history for scrolling waveform effect
  private amplitudeHistory: number[] = [];
  private maxHistoryLength: number = 0;
  private lastUpdateTime: number = 0;
  private updateInterval: number = 100; // Update every 100ms for smooth scrolling

  isTranscribing = computed(() => this.voiceToTextService.isWorking(this.sessionId));

  constructor(private voiceToTextService: VoiceToTextService) {
    effect(() => {
      if (this.isTranscribing()) {
        setTimeout(() => {
          this.startVisualization();
        }, 200);
      } else {
        this.stopVisualization();
      }
    });
  }

  ngOnInit(): void {
    this.setupCanvas();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private setupCanvas(): void {
    if (this.canvas?.nativeElement) {
      const canvas = this.canvas.nativeElement;

      canvas.width = this.canvasWidth;
      canvas.height = this.canvasHeight;

      // Calculate max history length based on canvas width and bar spacing
      const barSpacing = this.barWidth + this.barGap;
      this.maxHistoryLength = Math.floor(this.canvasWidth / barSpacing);
    }
  }

  private async startVisualization(): Promise<void> {
    try {
      // Pre-fill amplitude history with minimal visible dots
      this.amplitudeHistory = [];
      for (let i = 0; i < this.maxHistoryLength; i++) {
        this.amplitudeHistory.push(this.minimalAmplitude);
      }
      this.lastUpdateTime = 0;

      // Get media stream from voice service
      const stream = this.voiceToTextService.getCurrentMediaStream(this.sessionId);

      if (!stream) {
        console.warn('⚠️ No media stream available for waveform visualization');
        return;
      }

      // Setup audio analysis
      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();

      // Configure analyser for responsive visualization
      this.analyser.fftSize = 512;
      this.analyser.smoothingTimeConstant = 0.3;

      const bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(bufferLength);

      // Connect media stream to analyser
      const source = this.audioContext.createMediaStreamSource(stream);
      source.connect(this.analyser);

      this.animate();
    } catch (error) {
      console.error('❌ Error starting waveform visualization:', error);
    }
  }

  private animate(): void {
    if (!this.analyser || !this.dataArray || !this.canvas?.nativeElement) {
      return;
    }

    const currentTime = performance.now();

    // Update amplitude history at intervals for smooth scrolling
    if (currentTime - this.lastUpdateTime >= this.updateInterval) {
      this.updateAmplitudeHistory();
      this.lastUpdateTime = currentTime;
    }

    this.drawWaveform();
    this.animationId = requestAnimationFrame(() => this.animate());
  }

  private updateAmplitudeHistory(): void {
    if (!this.analyser || !this.dataArray) return;

    // Get frequency data and calculate average amplitude
    this.analyser.getByteFrequencyData(this.dataArray);

    let sum = 0;
    for (let i = 0; i < this.dataArray.length; i++) {
      sum += this.dataArray[i];
    }
    const averageAmplitude = sum / this.dataArray.length / 255; // Normalize to 0-1

    // Scroll effect: shift left and add new amplitude
    this.amplitudeHistory.shift();
    this.amplitudeHistory.push(Math.max(averageAmplitude, this.minimalAmplitude));
  }

  private drawWaveform(): void {
    const canvas = this.canvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const centerY = canvas.height / 2;

    // Draw amplitude bars centered vertically
    for (let i = 0; i < this.amplitudeHistory.length; i++) {
      const amplitude = this.amplitudeHistory[i];
      const barHeight = amplitude * canvas.height;
      const halfBarHeight = barHeight / 2;

      const x = i * (this.barWidth + this.barGap);
      const y = centerY - halfBarHeight;

      ctx.fillStyle = this.waveColor;
      this.drawRoundedRect(ctx, x, y, this.barWidth, barHeight, 2);
    }
  }

  private drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.fill();
  }

  private stopVisualization(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.analyser = null;
    this.dataArray = null;

    // Keep final waveform visible briefly, then clear
    setTimeout(() => {
      if (this.canvas?.nativeElement) {
        const ctx = this.canvas.nativeElement.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
        }
      }
      this.amplitudeHistory = [];
    }, 1000);
  }

  private cleanup(): void {
    this.stopVisualization();
  }
}
